import { useCallback, useEffect, useState, type FormEvent } from 'react';

interface OperationResourcesData {
  readonly people: readonly Record<string, unknown>[];
  readonly services: readonly Record<string, unknown>[];
  readonly documents: readonly Record<string, unknown>[];
  readonly communications: readonly Record<string, unknown>[];
  readonly baggage: readonly Record<string, unknown>[];
}

type ResourceKind = 'person' | 'service' | 'document' | 'communication' | 'baggage';

const labels: Record<ResourceKind, string> = {
  person: 'Pessoa a bordo',
  service: 'Serviço aeroportuário',
  document: 'Documento ou evidência',
  communication: 'Comunicação operacional',
  baggage: 'Bagagem e carga',
};

function localDateTime(): string {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

function initialDraft() {
  return {
    recordId: crypto.randomUUID(),
    idempotencyKey: crypto.randomUUID(),
    personKind: 'passenger',
    displayName: '',
    personFunction: '',
    documentStatus: 'pending',
    pob: false,
    operationLegId: '',
    serviceKind: 'handling',
    serviceStatus: 'requested',
    providerName: '',
    externalReference: '',
    notes: '',
    evidenceUrl: '',
    documentKind: '',
    operationDocumentStatus: 'pending',
    title: '',
    externalUrl: '',
    evidenceSummary: '',
    issuedAt: '',
    expiresAt: '',
    channel: 'email',
    contact: '',
    summary: '',
    communicatedAt: localDateTime(),
    baggageSummary: '',
    baggageCount: '',
    baggageWeightKg: '',
    cargoSummary: '',
    dangerousGoods: false,
  };
}

function value(row: Record<string, unknown>, key: string): string {
  const item = row[key];
  return typeof item === 'string' || typeof item === 'number' ? String(item) : '';
}

export function OperationResources({
  canEdit,
  operationId,
  organizationId,
  legs,
  onExpiredSession,
}: {
  readonly canEdit: boolean;
  readonly operationId: string;
  readonly organizationId: string;
  readonly legs: readonly {
    readonly id: string;
    readonly sequence: number;
    readonly originCode: string | null;
    readonly destinationCode: string | null;
  }[];
  readonly onExpiredSession: () => Promise<void>;
}) {
  const [data, setData] = useState<OperationResourcesData>();
  const [kind, setKind] = useState<ResourceKind>('person');
  const [draft, setDraft] = useState(initialDraft);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [feedback, setFeedback] = useState<string>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `/v1/organizations/${organizationId}/operations/${operationId}/resources`,
        { credentials: 'include' },
      );
      if (response.status === 401) {
        await onExpiredSession();
        return;
      }
      if (!response.ok) throw new Error(String(response.status));
      setData((await response.json()) as OperationResourcesData);
      setError(undefined);
    } catch {
      setError('Não foi possível carregar pessoas, serviços e evidências desta operação.');
    } finally {
      setLoading(false);
    }
  }, [onExpiredSession, operationId, organizationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const textOrNull = (entry: string) => entry.trim() || null;
  const body = () => {
    const identity = { recordId: draft.recordId, idempotencyKey: draft.idempotencyKey, kind };
    if (kind === 'person')
      return {
        ...identity,
        personKind: draft.personKind,
        displayName: draft.displayName,
        personFunction: textOrNull(draft.personFunction),
        documentStatus: draft.documentStatus,
        pob: draft.pob,
      };
    if (kind === 'service')
      return {
        ...identity,
        operationLegId: textOrNull(draft.operationLegId),
        serviceKind: draft.serviceKind,
        serviceStatus: draft.serviceStatus,
        providerName: textOrNull(draft.providerName),
        externalReference: textOrNull(draft.externalReference),
        notes: textOrNull(draft.notes),
        evidenceUrl: textOrNull(draft.evidenceUrl),
      };
    if (kind === 'document')
      return {
        ...identity,
        operationLegId: textOrNull(draft.operationLegId),
        documentKind: draft.documentKind,
        title: draft.title,
        documentStatus: draft.operationDocumentStatus,
        externalUrl: draft.externalUrl,
        evidenceSummary: textOrNull(draft.evidenceSummary),
        issuedAt: textOrNull(draft.issuedAt),
        expiresAt: textOrNull(draft.expiresAt),
      };
    if (kind === 'communication')
      return {
        ...identity,
        operationLegId: textOrNull(draft.operationLegId),
        channel: draft.channel,
        contact: draft.contact,
        summary: draft.summary,
        evidenceUrl: textOrNull(draft.evidenceUrl),
        communicatedAt: new Date(draft.communicatedAt).toISOString(),
      };
    return {
      ...identity,
      baggageSummary: textOrNull(draft.baggageSummary),
      baggageCount: draft.baggageCount ? Number(draft.baggageCount) : null,
      baggageWeightKg: draft.baggageWeightKg ? Number(draft.baggageWeightKg) : null,
      cargoSummary: textOrNull(draft.cargoSummary),
      dangerousGoods: draft.dangerousGoods,
    };
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError(undefined);
    setFeedback(undefined);
    try {
      const response = await fetch(
        `/v1/organizations/${organizationId}/operations/${operationId}/resources`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body()),
        },
      );
      if (response.status === 401) {
        await onExpiredSession();
        return;
      }
      if (!response.ok) {
        setError(
          response.status === 409
            ? 'A operação já foi encerrada ou houve conflito no envio.'
            : 'Revise os campos deste registro.',
        );
        return;
      }
      setData((await response.json()) as OperationResourcesData);
      setDraft(initialDraft());
      setFeedback(`${labels[kind]} registrado com autoria e horário.`);
    } catch {
      setError('Não foi possível salvar. Os dados foram mantidos para uma nova tentativa.');
    } finally {
      setSaving(false);
    }
  };

  const update = (field: string, next: string | boolean) =>
    setDraft((current) => ({ ...current, [field]: next }));
  const legSelect = (
    <label>
      Trecho
      <select
        value={draft.operationLegId}
        onChange={(event) => update('operationLegId', event.target.value)}
      >
        <option value="">Toda a operação</option>
        {legs.map((leg) => (
          <option key={leg.id} value={leg.id}>
            Trecho {leg.sequence} · {leg.originCode ?? 'ZZZZ'}–{leg.destinationCode ?? 'ZZZZ'}
          </option>
        ))}
      </select>
    </label>
  );

  const rows = [
    ...(data?.people ?? []).map((row) => ({
      key: `person-${value(row, 'id')}`,
      title: value(row, 'display_name'),
      detail: `${value(row, 'person_kind') === 'crew' ? 'Tripulante' : 'Passageiro'} · documento ${value(row, 'document_status')}`,
    })),
    ...(data?.services ?? []).map((row) => ({
      key: `service-${value(row, 'id')}`,
      title: value(row, 'service_kind'),
      detail: `${value(row, 'service_status')} · ${value(row, 'provider_name') || 'fornecedor não informado'}`,
    })),
    ...(data?.documents ?? []).map((row) => ({
      key: `document-${value(row, 'id')}`,
      title: value(row, 'title'),
      detail: `${value(row, 'document_kind')} · ${value(row, 'document_status')}`,
    })),
    ...(data?.communications ?? []).map((row) => ({
      key: `communication-${value(row, 'id')}`,
      title: value(row, 'contact'),
      detail: `${value(row, 'channel')} · ${value(row, 'summary')}`,
    })),
    ...(data?.baggage ?? []).map((row) => ({
      key: `baggage-${value(row, 'id')}`,
      title: 'Bagagem e carga',
      detail: `${value(row, 'baggage_count') || '0'} volumes · ${value(row, 'baggage_weight_kg') || '0'} kg`,
    })),
  ];

  return (
    <section
      className="workflowSection operationResources"
      id="operation-resources"
      aria-labelledby="operation-resources-title"
    >
      <header className="sectionHeader">
        <div>
          <span className="sectionEyebrow">Complementos operacionais</span>
          <h2 id="operation-resources-title">Pessoas, serviços e evidências</h2>
          <p>
            Centralize os registros que antes ficavam espalhados em planilhas, mensagens e links.
          </p>
        </div>
        <span>{rows.length} registros</span>
      </header>
      {loading ? <p role="status">Carregando registros…</p> : null}
      {error ? (
        <p className="inlineError" role="alert">
          {error}
        </p>
      ) : null}
      {feedback ? (
        <p className="invitationFeedback invitationFeedback-success" role="status">
          {feedback}
        </p>
      ) : null}
      {canEdit ? (
        <form className="operationResourceForm" onSubmit={(event) => void submit(event)}>
          <label className="resourceKind">
            Adicionar
            <select value={kind} onChange={(event) => setKind(event.target.value as ResourceKind)}>
              {Object.entries(labels).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <div className="resourceFormGrid">
            {kind === 'person' ? (
              <>
                <label>
                  Tipo
                  <select
                    value={draft.personKind}
                    onChange={(event) => update('personKind', event.target.value)}
                  >
                    <option value="passenger">Passageiro</option>
                    <option value="crew">Tripulante</option>
                  </select>
                </label>
                <label>
                  Nome
                  <input
                    required
                    minLength={2}
                    value={draft.displayName}
                    onChange={(event) => update('displayName', event.target.value)}
                  />
                </label>
                <label>
                  Função
                  <input
                    value={draft.personFunction}
                    onChange={(event) => update('personFunction', event.target.value)}
                  />
                </label>
                <label>
                  Situação do documento
                  <select
                    value={draft.documentStatus}
                    onChange={(event) => update('documentStatus', event.target.value)}
                  >
                    <option value="pending">Pendente</option>
                    <option value="valid">Válido</option>
                    <option value="verified">Verificado</option>
                    <option value="missing">Ausente</option>
                    <option value="expired">Vencido</option>
                  </select>
                </label>
                <label className="checkField">
                  <input
                    type="checkbox"
                    checked={draft.pob}
                    onChange={(event) => update('pob', event.target.checked)}
                  />
                  Considerar no total a bordo
                </label>
              </>
            ) : null}
            {kind === 'service' ? (
              <>
                {legSelect}
                <label>
                  Serviço
                  <select
                    value={draft.serviceKind}
                    onChange={(event) => update('serviceKind', event.target.value)}
                  >
                    <option value="handling">Handling</option>
                    <option value="fuel">Abastecimento</option>
                    <option value="catering">Catering</option>
                    <option value="ground_transport">Transporte terrestre</option>
                    <option value="slot_ppr">Slot/PPR</option>
                  </select>
                </label>
                <label>
                  Status
                  <select
                    value={draft.serviceStatus}
                    onChange={(event) => update('serviceStatus', event.target.value)}
                  >
                    <option value="requested">Solicitado</option>
                    <option value="confirmed">Confirmado</option>
                    <option value="declined">Recusado</option>
                    <option value="not_required">Não necessário</option>
                  </select>
                </label>
                <label>
                  Fornecedor
                  <input
                    value={draft.providerName}
                    onChange={(event) => update('providerName', event.target.value)}
                  />
                </label>
                <label>
                  Referência externa
                  <input
                    value={draft.externalReference}
                    onChange={(event) => update('externalReference', event.target.value)}
                  />
                </label>
                <label>
                  Link da evidência (HTTPS)
                  <input
                    type="url"
                    placeholder="https://"
                    value={draft.evidenceUrl}
                    onChange={(event) => update('evidenceUrl', event.target.value)}
                  />
                </label>
              </>
            ) : null}
            {kind === 'document' ? (
              <>
                {legSelect}
                <label>
                  Tipo de documento
                  <input
                    required
                    minLength={2}
                    value={draft.documentKind}
                    onChange={(event) => update('documentKind', event.target.value)}
                  />
                </label>
                <label>
                  Título
                  <input
                    required
                    minLength={2}
                    value={draft.title}
                    onChange={(event) => update('title', event.target.value)}
                  />
                </label>
                <label>
                  Situação
                  <select
                    value={draft.operationDocumentStatus}
                    onChange={(event) => update('operationDocumentStatus', event.target.value)}
                  >
                    <option value="pending">Pendente</option>
                    <option value="provided">Fornecido</option>
                    <option value="expired">Vencido</option>
                    <option value="rejected">Rejeitado</option>
                  </select>
                </label>
                <label>
                  Link HTTPS
                  <input
                    required
                    type="url"
                    value={draft.externalUrl}
                    onChange={(event) => update('externalUrl', event.target.value)}
                  />
                </label>
                <label>
                  Emissão
                  <input
                    type="date"
                    value={draft.issuedAt}
                    onChange={(event) => update('issuedAt', event.target.value)}
                  />
                </label>
                <label>
                  Validade
                  <input
                    type="date"
                    min={draft.issuedAt || undefined}
                    value={draft.expiresAt}
                    onChange={(event) => update('expiresAt', event.target.value)}
                  />
                </label>
              </>
            ) : null}
            {kind === 'communication' ? (
              <>
                {legSelect}
                <label>
                  Canal
                  <select
                    value={draft.channel}
                    onChange={(event) => update('channel', event.target.value)}
                  >
                    <option value="email">E-mail</option>
                    <option value="phone">Telefone</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="radio">Rádio</option>
                    <option value="portal">Portal</option>
                    <option value="other">Outro</option>
                  </select>
                </label>
                <label>
                  Contato
                  <input
                    required
                    minLength={2}
                    value={draft.contact}
                    onChange={(event) => update('contact', event.target.value)}
                  />
                </label>
                <label>
                  Quando
                  <input
                    required
                    type="datetime-local"
                    value={draft.communicatedAt}
                    onChange={(event) => update('communicatedAt', event.target.value)}
                  />
                </label>
                <label>
                  Link da evidência (HTTPS)
                  <input
                    type="url"
                    placeholder="https://"
                    value={draft.evidenceUrl}
                    onChange={(event) => update('evidenceUrl', event.target.value)}
                  />
                </label>
              </>
            ) : null}
            {kind === 'baggage' ? (
              <>
                <label>
                  Volumes
                  <input
                    min="0"
                    type="number"
                    value={draft.baggageCount}
                    onChange={(event) => update('baggageCount', event.target.value)}
                  />
                </label>
                <label>
                  Peso (kg)
                  <input
                    min="0"
                    step="0.01"
                    type="number"
                    value={draft.baggageWeightKg}
                    onChange={(event) => update('baggageWeightKg', event.target.value)}
                  />
                </label>
                <label>
                  Resumo da bagagem
                  <input
                    value={draft.baggageSummary}
                    onChange={(event) => update('baggageSummary', event.target.value)}
                  />
                </label>
                <label>
                  Resumo da carga
                  <input
                    value={draft.cargoSummary}
                    onChange={(event) => update('cargoSummary', event.target.value)}
                  />
                </label>
              </>
            ) : null}
          </div>
          {kind === 'service' ? (
            <label>
              Observações
              <textarea
                value={draft.notes}
                onChange={(event) => update('notes', event.target.value)}
              />
            </label>
          ) : null}
          {kind === 'communication' ? (
            <label>
              Resumo da comunicação
              <textarea
                required
                minLength={3}
                value={draft.summary}
                onChange={(event) => update('summary', event.target.value)}
              />
            </label>
          ) : null}
          {kind === 'document' ? (
            <label>
              Resumo da evidência
              <textarea
                value={draft.evidenceSummary}
                onChange={(event) => update('evidenceSummary', event.target.value)}
              />
            </label>
          ) : null}
          {kind === 'baggage' ? (
            <label className="checkField">
              <input
                type="checkbox"
                checked={draft.dangerousGoods}
                onChange={(event) => update('dangerousGoods', event.target.checked)}
              />
              Carga perigosa informada
            </label>
          ) : null}
          <button className="primaryButton" disabled={saving} type="submit">
            {saving ? 'Registrando…' : `Registrar ${labels[kind].toLowerCase()}`}
          </button>
        </form>
      ) : null}
      <div className="resourceRecordList">
        {rows.map((row) => (
          <article key={row.key}>
            <strong>{row.title}</strong>
            <span>{row.detail}</span>
          </article>
        ))}
        {!loading && rows.length === 0 ? <p>Nenhum complemento registrado ainda.</p> : null}
      </div>
    </section>
  );
}
